import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  Lightbulb as TipIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Healing as PetCareIcon,
  School as SchoolIcon,
  Favorite as HeartIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import FirestoreTest from './FirestoreTest';
import { stickerTrackingService, PlanType } from '../services/stickerTrackingService';

import { DynamicRoadmapConfig, DailyActivity } from '../services/dynamicRoadmapGenerator';

interface FlexibleRoadmapProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  roadmap: DynamicRoadmapConfig;
  userLanguage?: 'zh-CN' | 'es' | 'fr' | 'en';
  isArtifactView?: boolean; // New prop for artifact view mode
  onAskQuestion?: (context: import('../types/planQa').PlanQaContext) => void; // New prop for Plan Q&A
  onRoadmapCompleted?: (roadmap: DynamicRoadmapConfig) => void; // New prop for completion reflection
  showStickerStats?: boolean; // New prop for sticker stats dialog
  onStickerStatsOpen?: () => void; // New prop for opening sticker stats
  onStickerStatsClose?: () => void; // New prop for closing sticker stats
}

interface Progress {
  day: number;
  stickers: number;
  completedActivities: string[];
  lastCompleted?: Date;
  notes?: string;
}

const FlexibleRoadmap: React.FC<FlexibleRoadmapProps> = ({
  open,
  onClose,
  userId,
  roadmap,
  userLanguage,
  isArtifactView = false,
  onAskQuestion,
  onRoadmapCompleted,
  showStickerStats = false,
  onStickerStatsOpen,
  onStickerStatsClose
}) => {
  // Soft-wrap long titles at a natural delimiter or around target length
  const formatTitleForWrap = (title: string): string => {
    if (!title) return '';
    const lower = title.toLowerCase();
    const planIdx = lower.indexOf(' plan');
    if (planIdx !== -1) {
      const insertPos = planIdx + ' plan'.length;
      return title.slice(0, insertPos) + '\u200b' + title.slice(insertPos);
    }
    const target = 40; // soft wrap target
    // prefer a space before target but not too far left
    let leftSpace = title.lastIndexOf(' ', target);
    if (leftSpace >= 36) {
      return title.slice(0, leftSpace + 1) + '\u200b' + title.slice(leftSpace + 1);
    }
    // otherwise pick first space after target but not too far right
    const rightSpace = title.indexOf(' ', target);
    if (rightSpace !== -1 && rightSpace <= 48) {
      return title.slice(0, rightSpace + 1) + '\u200b' + title.slice(rightSpace + 1);
    }
    return title;
  };
  // Function to check and award retroactive stickers for completed plans
  const checkAndAwardRetroactiveStickers = async (roadmap: any, progress: any) => {
    try {
      console.log('🔄 CHECKING FOR RETROACTIVE STICKERS:', {
        planId: roadmap.id,
        planTitle: roadmap.title,
        completedActivities: progress.completedActivities
      });

      // Check if user has any sticker tracking data for this plan
      const planStickerEventsRef = collection(db, 'users', userId, 'plans', roadmap.id, 'stickerEvents');
      const stickerEventsSnapshot = await getDocs(planStickerEventsRef);

      if (stickerEventsSnapshot.empty) {
        console.log('🔄 NO STICKER EVENTS FOUND - Awarding retroactive stickers');

        // Extract completed days from progress
        const completedDays = progress.completedActivities
          .filter((activity: string) => activity.startsWith('day-'))
          .map((activity: string) => parseInt(activity.replace('day-', '')))
          .sort((a: number, b: number) => a - b);

        console.log('🔄 COMPLETED DAYS TO AWARD:', completedDays);

        if (completedDays.length > 0) {
          await stickerTrackingService.retroactivelyAwardStickers(
            userId,
            roadmap.id,
            roadmap.title,
            roadmap.category as PlanType,
            completedDays,
            userLanguage
          );
          console.log('✅ RETROACTIVE STICKERS AWARDED SUCCESSFULLY');
        }
      } else {
        console.log('🔄 STICKER EVENTS ALREADY EXIST - No retroactive awarding needed');
      }
    } catch (error) {
      console.error('❌ Error checking retroactive stickers:', error);
    }
  };

  const [progress, setProgress] = useState<Progress>({
    day: 1,
    stickers: 0,
    completedActivities: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCompletionCelebration, setShowCompletionCelebration] = useState(false);
  const [expandedDay, setExpandedDay] = useState<number | false>(1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Debug: Track component lifecycle and state
  console.log('🚀 FlexibleRoadmap COMPONENT MOUNTED/RENDERED:', {
    roadmapId: roadmap?.id,
    open,
    showCompletionCelebration: showCompletionCelebration // Use actual state value
  });

  // Use userLanguage prop if provided, otherwise detect from roadmap content
  const isChinese = userLanguage === 'zh-CN' || /[\u4e00-\u9fff]/.test(roadmap.title || roadmap.description || '');
  const isSpanish = userLanguage === 'es' || /[áéíóúñü]/.test((roadmap.title || roadmap.description || '').toLowerCase());
  const isFrench = userLanguage === 'fr' || /[àâäéèêëïîôöùûüÿç]/.test((roadmap.title || roadmap.description || '').toLowerCase());



  // Helper function to translate activity prefixes (Goal:, Activity:, Tip:)
  const translateActivityText = (text: string): string => {
    let translatedText = text;

    // Replace Goal: prefix
    if (text.includes('🎯 Goal:')) {
      translatedText = text.replace('🎯 Goal:', `🎯 ${getText('goal')}:`);
    }

    // Replace Activity: prefix
    if (text.includes('📝 Activity:')) {
      translatedText = text.replace('📝 Activity:', `📝 ${getText('activity')}:`);
    }

    // Replace Tip: prefix (both variations)
    if (text.includes('💡 Tip:')) {
      translatedText = text.replace('💡 Tip:', `💡 ${getText('tip')}:`);
    }
    if (text.includes('💡 Suggestion/Tip:')) {
      translatedText = text.replace('💡 Suggestion/Tip:', `💡 ${getText('tip')}:`);
    }

    return translatedText;
  };

  // Helper function to determine activity context type
  const getActivityContextType = (activity: string): 'goal' | 'activity' | 'tip' | 'parent_sharing' | 'general' => {
    if (activity.includes('🎯 Goal:')) return 'goal';
    if (activity.includes('📝 Activity:')) return 'activity';
    if (activity.includes('💡 Tip:') || activity.includes('💡 Suggestion/Tip:')) return 'tip';
    if (activity.includes('🗣️') || activity.toLowerCase().includes('parent') || activity.toLowerCase().includes('share')) return 'parent_sharing';
    return 'general';
  };

  // Handle asking question about a specific activity
  const handleAskQuestionAboutActivity = (day: DailyActivity, activity: string, activityIndex: number) => {
    if (!onAskQuestion) return;

    const contextType = getActivityContextType(activity);
    const planQaContext: import('../types/planQa').PlanQaContext = {
      planId: roadmap.id || `roadmap-${Date.now()}`,
      day: day.day,
      contextType,
      contextElement: translateActivityText(activity),
      planTitle: roadmap.title,
      planDescription: roadmap.description,
      userQuestion: '' // Will be filled by user in the Q&A tab
    };

    onAskQuestion(planQaContext);
  };

  const totalDays = roadmap?.dailyActivities?.length || 0;

  // FORCE DEBUG: Show current state every render
  console.log('🔍 ROADMAP STATE DEBUG:', {
    totalDays: totalDays,
    currentProgress: progress,
    progressCompletedCount: progress.completedActivities?.length || 0,
    isFullyCompleted: progress.completedActivities?.length === totalDays,
    finalDayKey: `day-${totalDays}`,
    finalDayInProgress: progress.completedActivities?.includes(`day-${totalDays}`)
  });

  // Debug: Track component lifecycle
  useEffect(() => {
    console.log('🚀 FlexibleRoadmap COMPONENT MOUNTED');
    return () => {
      console.log('🚀 FlexibleRoadmap COMPONENT UNMOUNTED');
    };
  }, []);

  // WORKING CELEBRATION: Pure HTML implementation that bypasses Material-UI
  useEffect(() => {
    if (showCompletionCelebration) {
      console.log('🎉 WORKING CELEBRATION: Creating celebration overlay');

      // Create celebration overlay
      const overlay = document.createElement('div');
      overlay.id = 'celebration-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: linear-gradient(135deg, rgba(76, 175, 80, 0.95) 0%, rgba(139, 195, 74, 0.95) 100%);
        z-index: 999999999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: 'Arial', sans-serif;
        pointer-events: auto;
        animation: fadeIn 0.5s ease-out;
      `;

      // Create main content container
      const container = document.createElement('div');
      container.style.cssText = `
        text-align: center;
        background: white;
        padding: 40px;
        border-radius: 20px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        border: 3px solid #FFD700;
        max-width: 500px;
        animation: celebrationBounce 1s ease-out infinite alternate;
      `;

      // Create title
      const title = document.createElement('h1');
      title.style.cssText = `
        color: #4CAF50;
        font-size: 48px;
        margin: 0 0 20px 0;
        font-weight: bold;
        text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
      `;
      title.innerHTML = `🎉 ${getText('amazing')} 🎉`;

      // Create message
      const message = document.createElement('p');
      message.style.cssText = `
        color: #333;
        font-size: 18px;
        margin: 0 0 20px 0;
        line-height: 1.5;
      `;
      message.innerHTML = `${getText('congratsCompleted')} &quot;${roadmap.title}&quot; ${getText('planText')}<br/>${getText('bonusSticker')}`;

      // Create sticker display
      const stickerDisplay = document.createElement('div');
      stickerDisplay.style.cssText = `
        font-size: 24px;
        color: #4CAF50;
        font-weight: bold;
        margin: 20px 0;
      `;
      stickerDisplay.innerHTML = getText('celebrationStickers');

      // Create close button
      const closeBtn = document.createElement('button');
      closeBtn.style.cssText = `
        background: #4CAF50;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.3s ease;
      `;
      closeBtn.innerHTML = getText('continueButton');
      closeBtn.onclick = () => {
        document.body.removeChild(overlay);
        setShowCompletionCelebration(false);

        // Trigger completion reflection flow
        if (onRoadmapCompleted) {
          console.log('🎉 TRIGGERING COMPLETION REFLECTION:', roadmap.title);
          onRoadmapCompleted(roadmap);
        }
      };

      // Assemble components
      container.appendChild(title);
      container.appendChild(message);
      container.appendChild(stickerDisplay);
      container.appendChild(closeBtn);
      overlay.appendChild(container);

      // Add CSS animations
      const style = document.createElement('style');
      style.innerHTML = `
        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes celebrationBounce {
          0% { transform: scale(1); }
          100% { transform: scale(1.05); }
        }
      `;
      document.head.appendChild(style);

      // Add to DOM
      document.body.appendChild(overlay);
      console.log('🎉 WORKING CELEBRATION: Pure HTML celebration added to DOM');

      // Auto-remove after 30 seconds
      const timeoutId = setTimeout(() => {
        if (document.getElementById('celebration-overlay')) {
          document.body.removeChild(overlay);
          setShowCompletionCelebration(false);
        }
      }, 30000);

      return () => {
        // Cleanup
        const existingOverlay = document.getElementById('celebration-overlay');
        if (existingOverlay) {
          document.body.removeChild(existingOverlay);
        }
        clearTimeout(timeoutId);
      };
    }
  }, [showCompletionCelebration, roadmap.title]);

  // REMOVED: Fixed-positioned badge - now using inline badge next to title
  // Clean up any existing fixed badges
  useEffect(() => {
    const existingBadge = document.getElementById('permanent-completion-badge');
    if (existingBadge) {
      document.body.removeChild(existingBadge);
    }
  }, []);

  // Multi-language text mappings
  const getText = (key: string) => {
    if (isChinese) {
      const chineseTexts: Record<string, string> = {
        goals: '目标',
        tips: '提示',
        day: '第',
        daySuffix: '天',
        progress: '进度',
        activitiesForToday: '今日活动',
        materialsNeeded: '所需材料',
        stickersEarned: '获得贴纸',
        daysCompleted: '天已完成',
        close: '关闭',
        markAsDone: '标记完成',
        saving: '保存中...',
        continueBuilding: '继续建立之前的进度',
        backToChat: '返回聊天',
        estimatedTime: '预计时间',
        minutes: '分钟',
        of: '的',
        dayLabel: '第{day}天',
        debug: '调试',
        completedBadge: '🎉 已完成! 🎉',
        amazing: '太棒了!',
        congratsCompleted: '恭喜! 您已完成',
        planText: '计划!',
        bonusSticker: '您获得了奖励贴纸!',
        continueButton: '继续',
        welcomeMessage: '欢迎来到计划模块！这里每天都有小目标，活动和一些建议和贴士。有疑问？和助手聊聊，告诉孩子的表现，就能得到更贴心的指导。',
        celebrationStickers: '🏆 +2 贴纸! 🏆',
        goal: '目标',
        activity: '活动',
        tip: '提示'
      };
      return chineseTexts[key] || key;
    } else if (isSpanish) {
      const spanishTexts: Record<string, string> = {
        goals: 'Objetivos',
        tips: 'Consejos',
        day: 'Día',
        daySuffix: '',
        progress: 'Progreso',
        activitiesForToday: 'Actividades para hoy',
        materialsNeeded: 'Materiales necesarios',
        stickersEarned: 'Calcomanías ganadas',
        daysCompleted: 'días completados',
        close: 'Cerrar',
        markAsDone: 'Marcar como hecho',
        saving: 'Guardando...',
        continueBuilding: 'Continuar construyendo el progreso anterior',
        backToChat: 'Volver al Chat',
        estimatedTime: 'Tiempo estimado',
        minutes: 'minutos',
        of: 'de',
        dayLabel: 'Día {day}',
        debug: 'Depurar',
        completedBadge: '🎉 COMPLETADO! 🎉',
        amazing: '¡INCREÍBLE!',
        congratsCompleted: '¡Felicidades! Has completado',
        planText: 'plan!',
        bonusSticker: '¡Ganaste una calcomanía de bonificación!',
        continueButton: 'Continuar',
        welcomeMessage: '¡Bienvenido al módulo de planificación! Aquí tienes pequeños objetivos, actividades y algunos consejos y sugerencias cada día. ¿Tienes preguntas? Habla con el asistente, cuéntale sobre el progreso de tu hijo/a y obtendrás orientación más personalizada.',
        celebrationStickers: '🏆 +2 Calcomanías! 🏆',
        goal: 'Objetivo',
        activity: 'Actividad',
        tip: 'Consejo'
      };
      return spanishTexts[key] || key;
    } else if (isFrench) {
      const frenchTexts: Record<string, string> = {
        goals: 'Objectifs',
        tips: 'Conseils',
        day: 'Jour',
        daySuffix: '',
        progress: 'Progrès',
        activitiesForToday: 'Activités pour aujourd\'hui',
        materialsNeeded: 'Matériaux nécessaires',
        stickersEarned: 'Autocollants gagnés',
        daysCompleted: 'jours terminés',
        close: 'Fermer',
        markAsDone: 'Marquer comme terminé',
        saving: 'Enregistrement...',
        continueBuilding: 'Continuer à construire sur les progrès précédents',
        backToChat: 'Retour au Chat',
        estimatedTime: 'Temps estimé',
        minutes: 'minutes',
        of: 'de',
        dayLabel: 'Jour {day}',
        debug: 'Déboguer',
        completedBadge: '🎉 TERMINÉ! 🎉',
        amazing: 'INCROYABLE!',
        congratsCompleted: 'Félicitations! Vous avez terminé',
        planText: 'plan!',
        bonusSticker: 'Vous avez gagné un autocollant bonus!',
        continueButton: 'Continuer',
        welcomeMessage: 'Bienvenue dans le module de planification ! Ici vous avez de petits objectifs, des activités et quelques conseils et astuces chaque jour. Des questions ? Parlez avec l\'assistant, parlez-lui des progrès de votre enfant et vous obtiendrez des conseils plus personnalisés.',
        celebrationStickers: '🏆 +2 Autocollants! 🏆',
        goal: 'Objectif',
        activity: 'Activité',
        tip: 'Conseil'
      };
      return frenchTexts[key] || key;
    } else {
      // English
      const englishTexts: Record<string, string> = {
        goals: 'Goals',
        tips: 'Tips',
        day: 'Day',
        daySuffix: '',
        progress: 'Progress',
        activitiesForToday: 'Activities for today',
        materialsNeeded: 'Materials needed',
        stickersEarned: 'Stickers earned',
        daysCompleted: 'days completed',
        close: 'Close',
        markAsDone: 'Mark as done',
        saving: 'Saving...',
        continueBuilding: 'Continue building on previous progress',
        backToChat: 'Back to Chat',
        estimatedTime: 'Estimated time',
        minutes: 'minutes',
        of: 'of',
        dayLabel: 'Day {day}',
        debug: 'Debug',
        completedBadge: '🎉 COMPLETED! 🎉',
        amazing: 'AMAZING!',
        congratsCompleted: 'Congrats! You\'ve completed the',
        planText: 'plan!',
        bonusSticker: 'You earned a bonus sticker!',
        continueButton: 'Continue',
        welcomeMessage: 'Welcome to the planning module! Here you have small goals, activities and some suggestions and tips every day. Have questions? Chat with the assistant, tell about your child\'s progress, and you\'ll get more personalized guidance.',
        celebrationStickers: '🏆 +2 Stickers! 🏆',
        goal: 'Goal',
        activity: 'Activity',
        tip: 'Tip'
      };
      return englishTexts[key] || key;
    }
  };

  // Helper function to format day labels with proper translations
  const getDayLabel = (dayNumber: number) => {
    if (isChinese) {
      return `第${dayNumber}天`;
    } else if (isSpanish) {
      return `Día ${dayNumber}`;
    } else if (isFrench) {
      return `Jour ${dayNumber}`;
    } else {
      return `Day ${dayNumber}`;
    }
  };

  // const totalDays = roadmap.duration; // Moved up to line 79

  // Load progress from Firestore
  useEffect(() => {
    const fetchProgress = async () => {
      if (!userId || !roadmap.id) return;

      try {
        const ref = doc(db, 'users', userId, 'plans', roadmap.id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as Progress;
          setProgress(data);
          // Set the current day as expanded
          setExpandedDay(data.day);

          // Check if roadmap is already completed and show celebration
          if (data.completedActivities && data.completedActivities.length >= totalDays) {
            console.log('🎉 ROADMAP ALREADY COMPLETED - Auto-showing celebration');
            console.log('🎉 Final day key:', `day-${totalDays}`);
            console.log('🎉 Final day in completed activities:', data.completedActivities.includes(`day-${totalDays}`));

            // Check and award retroactive stickers for this completed plan
            checkAndAwardRetroactiveStickers(roadmap, data);

            // Force multiple state updates to ensure re-render
            setShowCompletionCelebration(true);
            setProgress({ ...data, stickers: data.stickers + 0.01 });

            // Force additional re-renders
            setTimeout(() => {
              console.log('🔄 First force re-render');
              setProgress({ ...data, stickers: data.stickers + 0.02 });
            }, 50);

            setTimeout(() => {
              console.log('🔄 Second force re-render');
              setProgress({ ...data, stickers: data.stickers + 0.03 });
            }, 150);

            setTimeout(() => {
              console.log('🔄 Third force re-render');
              setProgress({ ...data, stickers: data.stickers + 0.04 });
            }, 250);
          }
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
    };

    // Don't fetch progress if we're showing completion celebration
    // This prevents the useEffect from interfering with the celebration state
    if (open && !showCompletionCelebration) {
      console.log('🔄 Fetching progress (celebration not active)');
      console.log('🗂️ ROADMAP DEBUG INFO:', {
        roadmapTitle: roadmap?.title,
        totalDays: roadmap?.dailyActivities?.length || 0,
        userId: userId,
        userLanguage: userLanguage
      });
      fetchProgress();
    } else if (open && showCompletionCelebration) {
      console.log('🎉 Skipping progress fetch (celebration active)');
    }
  }, [userId, roadmap.id, open, totalDays]); // Added totalDays to dependencies

  // Debug: Monitor completion celebration state changes
  useEffect(() => {
    console.log('🎉 showCompletionCelebration state changed to:', showCompletionCelebration);
  }, [showCompletionCelebration]);

  // Save progress to Firestore
  const saveProgress = async (newProgress: Progress) => {
    if (!userId || !roadmap.id) {
      console.error('💾 SAVE ERROR - Missing userId or roadmap.id:', { userId, roadmapId: roadmap.id });
      return;
    }

    try {
      setIsLoading(true);
      const ref = doc(db, 'users', userId, 'plans', roadmap.id);

      console.log('💾 SAVING TO FIRESTORE:', {
        path: `users/${userId}/plans/${roadmap.id}`,
        data: newProgress
      });

      await setDoc(ref, newProgress, { merge: true });
      setProgress(newProgress);

      console.log('💾 FIRESTORE SAVE SUCCESS - Progress saved:', newProgress);

      // Verify the save by reading it back
      const verifySnap = await getDoc(ref);
      if (verifySnap.exists()) {
        const savedData = verifySnap.data();
        console.log('✅ FIRESTORE VERIFY - Data confirmed saved:', savedData);
      } else {
        console.error('❌ FIRESTORE VERIFY - Document missing after save!');
      }

    } catch (error) {
      console.error('❌ FIRESTORE SAVE ERROR:', error);
      if (error instanceof Error) {
        console.error('❌ Error details:', error.message);
        console.error('❌ Error stack:', error.stack);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle completing a day
  const handleCompleteDay = async () => {
    // Prevent multiple simultaneous executions
    if (isLoading) {
      console.log('⚠️ PREVENTION: handleCompleteDay already in progress, ignoring...');
      return;
    }

    const nextDay = progress.day + 1;
    const isLastDay = nextDay > totalDays;

    try {
      // Award stickers using the new tracking system
      const stickerResult = await stickerTrackingService.awardDayCompletionStickers(
        userId,
        roadmap.id,
        roadmap.title,
        'dynamic', // Plan type for personalized roadmaps
        progress.day,
        totalDays,
        isLastDay,
        userLanguage
      );

      console.log('🏆 Stickers awarded:', stickerResult);

      // Mark current day as completed
      const dayKey = `day-${progress.day}`;

      // Check if this day is already completed to prevent duplicate completion
      if (progress.completedActivities.includes(dayKey)) {
        console.log('⚠️ ALREADY COMPLETED: Day already marked as completed, skipping...');
        return;
      }

      console.log('🎯 COMPLETING DAY - Current progress:', progress);
      console.log('🎯 COMPLETING DAY - Current day:', progress.day);
      console.log('🎯 COMPLETING DAY - Total days:', totalDays);
      console.log('🎯 COMPLETING DAY - Current completedActivities:', progress.completedActivities);

      // Clean up any existing duplicates and add the current day
      const cleanedActivities = progress.completedActivities.filter((item, index) =>
        progress.completedActivities.indexOf(item) === index
      ); // Remove duplicates
      const newCompletedActivities = cleanedActivities.includes(dayKey)
        ? cleanedActivities
        : [...cleanedActivities, dayKey];

      console.log('🔍 CLEANED ACTIVITIES:', {
        original: progress.completedActivities.length,
        cleaned: cleanedActivities.length,
        final: newCompletedActivities.length
      });

      if (nextDay <= totalDays) {
        // Regular day completion - move to next day
        const newProgress: Progress = {
          day: nextDay,
          stickers: progress.stickers + stickerResult.stickersEarned,
          completedActivities: newCompletedActivities,
          lastCompleted: new Date()
        };

        console.log('🎯 REGULAR DAY PROGRESS CREATED:', newProgress);

        // Update local state immediately to show green color
        setProgress(newProgress);

        // Save to database
        await saveProgress(newProgress);

        // Show confetti animation
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);

        // Expand the next day
        setExpandedDay(nextDay);
      } else {
        // If it's the last day, mark it as completed and give bonus sticker
        console.log('🎯 COMPLETING FINAL DAY - Current progress:', progress);
        console.log('🎯 COMPLETING FINAL DAY - Roadmap title:', roadmap.title);
        console.log('🎯 COMPLETING FINAL DAY - Current day:', progress.day);
        console.log('🎯 COMPLETING FINAL DAY - Total days:', totalDays);
        console.log('🎯 COMPLETING FINAL DAY - Current completedActivities:', progress.completedActivities);

        const finalProgress: Progress = {
          day: progress.day, // Keep the same day but mark as completed
          stickers: progress.stickers + stickerResult.stickersEarned, // Use stickers from tracking system
          completedActivities: newCompletedActivities,
          lastCompleted: new Date()
        };

        console.log('🎯 FINAL PROGRESS CREATED:', finalProgress);
        console.log('🎯 Day key being added:', dayKey);
        console.log('🎯 New completedActivities array:', newCompletedActivities);

        // Update local state immediately to show green color
        console.log('🔄 Updating local state with final progress');
        setProgress(finalProgress);

        // Force component re-render to ensure UI updates
        console.log('🔄 Forcing re-render with expanded day update');
        setExpandedDay(false); // Collapse all

        // Force multiple re-renders to ensure UI updates
        setTimeout(() => {
          console.log('🔄 First re-render - setting progress');
          setProgress({ ...finalProgress });
        }, 50);

        setTimeout(() => {
          console.log('🔄 Second re-render - expanding day');
          setExpandedDay(finalProgress.day);
        }, 150);

        setTimeout(() => {
          console.log('🔄 Third re-render - final update');
          setProgress({ ...finalProgress }); // Final update without decimal changes
        }, 250);

        // Show big completion celebration AFTER state update
        console.log('🎉 Setting completion celebration to true');
        setShowCompletionCelebration(true);

        // Add a small delay to ensure state is updated
        setTimeout(() => {
          console.log('🔄 State should be updated now, checking showCompletionCelebration');
        }, 100);

        // Save to database
        console.log('💾 Saving to database...');
        await saveProgress(finalProgress);
        console.log('💾 Database save completed');

        // Set timeout to hide celebration
        setTimeout(() => {
          console.log('🎉 Setting completion celebration to false after 60 seconds');
          setShowCompletionCelebration(false);
        }, 60000);
      }
    } catch (error) {
      console.error('❌ Error in handleCompleteDay:', error);
      // Still show confetti for user feedback even if sticker tracking fails
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
    }
  };

  // Handle expanding/collapsing days
  const handleDayExpansion = (day: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedDay(isExpanded ? day : false);
  };

  // Calculate progress percentage
  // If the current day is completed, show 100%, otherwise show progress
  const completedDays = progress.completedActivities.length;
  const progressPercentage = Math.min(100, Math.round((completedDays / totalDays) * 100));



  // Group days into weeks for better organization
  const weeks = [];
  for (let i = 0; i < totalDays; i += 7) {
    weeks.push(roadmap.dailyActivities.slice(i, i + 7));
  }

  // If it's artifact view, render inline content instead of dialog
  if (isArtifactView) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible'
      }}>
        {/* Header for artifact view */}
        <Box sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'visible',
          width: '100%',
          minWidth: 0,
          background: 'linear-gradient(135deg, #F4A261 0%, #E76F51 100%)',
          color: 'white'
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2, minWidth: 0 }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.02em',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  display: 'block',
                  maxWidth: '100%',
                  lineHeight: 1.25,
                  overflow: 'visible',
                  textOverflow: 'unset',
                  minWidth: 0,
                  fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.9rem', lg: '2.1rem' }
                }}
              >
                {formatTitleForWrap(roadmap.title)}
              </Typography>
              {/* Completed Badge - inline with title */}
              {progress.completedActivities.length >= totalDays && (
                <Box
                  sx={{
                    background: 'linear-gradient(45deg, #E76F51 0%, #F4A261 50%, #FFD700 100%)',
                    borderRadius: '20px',
                    px: 2,
                    py: 1,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    animation: 'badgePulse 3s ease-in-out infinite',
                    cursor: 'default',
                    '@keyframes badgePulse': {
                      '0%, 100%': {
                        transform: 'scale(1)',
                        boxShadow: '0 8px 25px rgba(255, 107, 53, 0.8)'
                      },
                      '50%': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 12px 35px rgba(255, 107, 53, 1), 0 0 15px #FFD700'
                      }
                    }
                  }}
                >
                  {getText('completedBadge')}
                </Box>
              )}
            </Box>

            {/* Sticker Statistics Button */}
            <Box display="flex" alignItems="center" gap={2} sx={{ flexShrink: 0 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <StarIcon sx={{ color: '#FFD700', fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                  {progress.stickers}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  console.log('📊 Analytics icon clicked, opening sticker stats...');
                  console.log('📊 Current showStickerStats state:', showStickerStats);
                  onStickerStatsOpen?.();
                  console.log('📊 Called onStickerStatsOpen');
                }}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  }
                }}
                title="View Sticker Statistics"
              >
                <AnalyticsIcon />
              </IconButton>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'white',
                flexShrink: 0,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{
            width: '100%',
            minWidth: 0,
            overflow: 'visible'
          }}>
            <Typography
              variant="h6"
              sx={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: 1.6,
                maxWidth: 'none',
                width: '100%',
                overflow: 'visible',
                textOverflow: 'unset',
                minWidth: 0,
                opacity: 0.95,
                fontWeight: 400,
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
              }}
            >
              {roadmap.description}
            </Typography>
          </Box>
        </Box>

        {/* Content for artifact view */}
        <Box sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 3
        }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  color: 'text.primary'
                }}
              >
                {getText('progress')}: {progress.completedActivities.length}/{roadmap.dailyActivities.length} {getText('daysCompleted')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  color: 'primary.main',
                  background: 'linear-gradient(135deg, #48B0A5 0%, #2A9D8F 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {Math.min(100, Math.round((progress.completedActivities.length / roadmap.dailyActivities.length) * 100))}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.round((progress.completedActivities.length / roadmap.dailyActivities.length) * 100))}
              sx={{
                height: 16,
                borderRadius: 8,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #F4A261 0%, #E76F51 100%)',
                  borderRadius: 8
                }
              }}
            />
          </Box>

          {/* Goals and Tips */}
          <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
            <Card sx={{
              flex: 1,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'linear-gradient(135deg, #E76F51 0%, #D84315 100%)',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 700,
                    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  <SchoolIcon sx={{ mr: 1.5, color: 'white', fontSize: 28 }} />
                  {getText('goals')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {roadmap.goals.map((goal, index) => (
                    <Chip
                      key={index}
                      label={goal}
                      size="medium"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        py: 1,
                        px: 1.5,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.3)',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Card sx={{
              flex: 1,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'linear-gradient(135deg, #48B0A5 0%, #2A9D8F 100%)',
              color: 'white',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 700,
                    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  <TipIcon sx={{ mr: 1.5, color: 'white', fontSize: 28 }} />
                  {getText('tips')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'white',
                    opacity: 0.95,
                    fontWeight: 500,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  {getText('welcomeMessage')}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Daily Activities */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
                background: 'linear-gradient(135deg, #264653 0%, #2A9D8F 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center'
              }}
            >
              {getText('activitiesForToday')}
            </Typography>
            {roadmap.dailyActivities.map((day, dayIndex) => {
              const dayNumber = dayIndex + 1;

              // Use same logic as the dialog view for proper completion detection
              const dayKey = `day-${dayNumber}`;
              const isInCompletedArray = progress.completedActivities.includes(dayKey);
              const isBeforeCurrentDay = dayNumber < progress.day;
              const isCompleted = isInCompletedArray || isBeforeCurrentDay;

              // Fix: Override completion status for final day
              const isFinalDayCompleted = dayNumber === totalDays && progress.completedActivities.includes(`day-${totalDays}`);
              const isCompletedOverride = isCompleted || isFinalDayCompleted;
              const isCurrentDay = dayNumber === progress.day && !isInCompletedArray && !isFinalDayCompleted;
              const isLocked = dayNumber > progress.day;

              return (
                <Accordion
                  key={dayNumber}
                  expanded={expandedDay === dayNumber}
                  onChange={() => setExpandedDay(expandedDay === dayNumber ? false : dayNumber)}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: isCurrentDay ? '0 8px 25px rgba(255, 193, 7, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      boxShadow: isCurrentDay ? '0 12px 35px rgba(255, 193, 7, 0.4)' : '0 8px 20px rgba(0,0,0,0.15)',
                      transform: 'translateY(-2px)'
                    },
                    ...(isLocked && {
                      bgcolor: 'grey.100',
                      '& .MuiAccordionSummary-root': {
                        color: 'text.disabled'
                      }
                    }),
                    ...(isCurrentDay && {
                      bgcolor: 'warning.light',
                      '& .MuiAccordionSummary-root': {
                        color: 'warning.contrastText'
                      }
                    }),
                    ...(isCompletedOverride && {
                      bgcolor: 'success.light',
                      '& .MuiAccordionSummary-root': {
                        color: 'success.contrastText'
                      }
                    })
                  }}
                >
                  <AccordionSummary expandIcon={isLocked ? null : <ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {isCompletedOverride ? (
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                      ) : isCurrentDay ? (
                        <StarIcon sx={{ color: 'warning.main' }} />
                      ) : (
                        <StarIcon sx={{ opacity: 0.3 }} />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        {(() => {
                          if (isChinese) {
                            return `第${dayNumber}天: ${day.title}`;
                          } else if (isSpanish) {
                            return `Día ${dayNumber}: ${day.title}`;
                          } else if (isFrench) {
                            return `Jour ${dayNumber}: ${day.title}`;
                          } else {
                            return `Day ${dayNumber}: ${day.title}`;
                          }
                        })()}
                      </Typography>
                      {day.estimatedTime && (
                        <Chip
                          icon={<TimeIcon />}
                          label={day.estimatedTime}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {day.description && day.description.trim() && !day.description.includes("Focus on today's specific goals and activities") && (
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {day.description}
                      </Typography>
                    )}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                        {getText('materialsNeeded')}:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {day.activities.map((activity, index) => {
                          // Determine chip color based on activity type
                          let chipColor: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "secondary";
                          if (activity.includes('🎯 Goal:')) chipColor = "error";  // Red for goals
                          else if (activity.includes('📝 Activity:')) chipColor = "info"; // Blue for activities  
                          else if (activity.includes('💡')) chipColor = "warning"; // Orange for tips

                          return (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: isCompletedOverride ? 'rgba(76, 175, 80, 0.15)' : 'grey.50',
                                border: isCompletedOverride ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                  backgroundColor: isCompletedOverride ? 'rgba(76, 175, 80, 0.25)' : 'grey.100'
                                }
                              }}
                            >
                              <Chip
                                label={translateActivityText(activity)}
                                size="small"
                                color={chipColor}
                                variant="outlined"
                                sx={{
                                  flex: 1,
                                  // Override colors for completed days (green background)
                                  ...(isCompletedOverride && {
                                    color: 'white !important',
                                    backgroundColor: 'rgba(76, 175, 80, 0.8) !important',
                                    borderColor: 'rgba(76, 175, 80, 1) !important',
                                    '& .MuiChip-label': {
                                      color: 'white !important'
                                    }
                                  })
                                }}
                              />
                              {onAskQuestion && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleAskQuestionAboutActivity(day, activity, index)}
                                  sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                      backgroundColor: 'primary.light'
                                    }
                                  }}
                                  title={getText('askQuestion') || 'Ask Question'}
                                >
                                  <QuestionAnswerIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    {/* Mark as Done button - only show for current day */}
                    {isCurrentDay && (
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 3,
                        pt: 3,
                        borderTop: 1,
                        borderColor: 'divider'
                      }}>
                        <Button
                          onClick={() => handleCompleteDay()}
                          variant="contained"
                          size="large"
                          disabled={isLoading}
                          startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                          sx={{
                            py: 2.5,
                            px: 5,
                            borderRadius: 6,
                            minWidth: 180,
                            background: 'linear-gradient(135deg, #48B0A5 0%, #2A9D8F 100%)',
                            boxShadow: '0 10px 30px rgba(0, 200, 83, 0.4), 0 4px 15px rgba(0, 200, 83, 0.2)',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                            textTransform: 'none',
                            letterSpacing: '0.5px',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                              transition: 'left 0.5s',
                            },
                            '&:hover': {
                              background: 'linear-gradient(135deg, #2A9D8F 0%, #48B0A5 50%, #87CEEB 100%)',
                              transform: 'translateY(-3px) scale(1.02)',
                              boxShadow: '0 15px 40px rgba(42, 157, 143, 0.6), 0 8px 25px rgba(42, 157, 143, 0.3)',
                              '&::before': {
                                left: '100%',
                              }
                            },
                            '&:active': {
                              transform: 'translateY(-1px) scale(1.01)',
                              boxShadow: '0 8px 25px rgba(0, 200, 83, 0.4)',
                              transition: 'all 0.1s'
                            }
                          }}
                        >
                          {isLoading ? getText('saving') : getText('markAsDone')}
                        </Button>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          {/* Stats */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <HeartIcon sx={{ mr: 2, color: 'error.main', fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                }}
              >
                {getText('stickersEarned')}: {Math.round(progress.stickers)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <TrophyIcon sx={{ mr: 2, color: 'warning.main', fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                }}
              >
                {progress.completedActivities.length}/{roadmap.dailyActivities.length} {getText('daysCompleted')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Simple Confetti Animation */}
        {showConfetti && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: 'success.main',
                animation: 'bounce 0.6s ease-in-out',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)',
                  },
                  '40%': {
                    transform: 'translateY(-30px)',
                  },
                  '60%': {
                    transform: 'translateY(-15px)',
                  },
                },
              }}
            >
              🎉
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Original dialog view for backward compatibility
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '90vh',
          position: 'relative'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: isMobile ? 2 : 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant={isMobile ? "h6" : "h5"} component="h2" sx={{ fontWeight: 'bold' }}>
            {roadmap.title.length > (isMobile ? 30 : 50) ? `${roadmap.title.substring(0, isMobile ? 30 : 50)}...` : roadmap.title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {roadmap.description.length > 100 ? `${roadmap.description.substring(0, 100)}...` : roadmap.description}
        </Typography>
      </DialogTitle>

      <DialogContent data-roadmap-container sx={{ p: isMobile ? 2 : 3, overflow: 'auto' }}>
        {/* Firestore Connection Test */}
        <FirestoreTest />

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="text.secondary">
              {getText('progress')}: {progress.completedActivities.length}/{totalDays} {getText('daysCompleted')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {progressPercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Roadmap Overview */}
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight="medium">
                    {getText('goals')}:
                  </Typography>
                </Box>
                {roadmap.goals.map((goal, index) => (
                  <Chip
                    key={index}
                    label={goal}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <TipIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="body2" fontWeight="medium">
                    {getText('tips')}:
                  </Typography>
                </Box>
                {roadmap.tips.map((tip, index) => (
                  <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    • {tip}
                  </Typography>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Weekly Progress */}
        {weeks.map((weekDays, weekIndex) => (
          <Card key={weekIndex} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                {getDayLabel(weekIndex + 1)}
              </Typography>

              {weekDays.map((day) => {
                // A day is completed if it's in the completedActivities array OR if it's less than the current day
                // For the last day, also check if it's been completed
                const dayKey = `day-${day.day}`;
                const isInCompletedArray = progress.completedActivities.includes(dayKey);
                const isBeforeCurrentDay = day.day < progress.day;
                const isCompleted = isInCompletedArray || isBeforeCurrentDay;

                // Fix: Override completion status for final day
                const isFinalDayCompleted = day.day === totalDays && progress.completedActivities.includes(`day-${totalDays}`);
                const isCompletedOverride = isCompleted || isFinalDayCompleted;
                const isCurrent = day.day === progress.day && !isInCompletedArray && !isFinalDayCompleted;
                const isLocked = day.day > progress.day;

                // Enhanced debug logging for completion issues
                console.log(`🔍 DAY ${day.day} STATUS CHECK:`, {
                  dayKey,
                  isInCompletedArray,
                  isBeforeCurrentDay,
                  isCompleted,
                  isCompletedOverride,
                  isFinalDayCompleted,
                  isCurrent,
                  isLocked,
                  progressDay: progress.day,
                  completedActivities: progress.completedActivities,
                  totalDays,
                  showCompletionCelebration
                });

                // SPECIAL LOGGING FOR FINAL DAY
                if (day.day === totalDays) {
                  console.log(`🚨 FINAL DAY ${day.day} DETAILED STATUS:`, {
                    shouldBeGreen: isCompletedOverride,
                    willUseSuccessColor: isCompletedOverride,
                    cardStyling: isCompletedOverride ? 'SUCCESS (GREEN)' : isCurrent ? 'WARNING (ORANGE)' : 'DEFAULT',
                    allCompletedActivities: progress.completedActivities,
                    finalDayKey: dayKey,
                    finalDayInArray: progress.completedActivities.includes(dayKey),
                    isCompleted,
                    isInCompletedArray,
                    isFinalDayCompleted,
                    isCurrent,
                    isLocked,
                    totalDays
                  });
                }

                return (
                  <Accordion
                    key={day.day}
                    data-day={day.day}
                    data-day-key={dayKey}
                    expanded={expandedDay === day.day}
                    onChange={handleDayExpansion(day.day)}
                    sx={{
                      mb: 1,
                      '&:before': { display: 'none' },
                      ...(isLocked && {
                        bgcolor: 'grey.100',
                        '& .MuiAccordionSummary-root': {
                          color: 'text.disabled'
                        }
                      }),
                      ...(isCurrent && {
                        bgcolor: 'warning.light',
                        '& .MuiAccordionSummary-root': {
                          color: 'warning.contrastText'
                        }
                      }),
                      ...(isCompletedOverride && {
                        bgcolor: 'success.light',
                        '& .MuiAccordionSummary-root': {
                          color: 'success.contrastText'
                        }
                      })
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                        <Box sx={{ mr: 2 }}>
                          {isCompleted ? (
                            <CheckCircleIcon color="success" sx={{ fontSize: 24 }} />
                          ) : isCurrent ? (
                            <StarIcon color="warning" sx={{ fontSize: 24 }} />
                          ) : (
                            <StarIcon sx={{ opacity: 0.3, fontSize: 24 }} />
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {getDayLabel(day.day)}: {day.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              lineHeight: 1.3
                            }}
                          >
                            {day.description === 'Continue building on previous progress' ? getText('continueBuilding') :
                              day.description && !day.description.includes("Focus on today's specific goals and activities") ? day.description : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                          <TimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">
                            {day.estimatedTime}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          {getText('activitiesForToday')}:
                        </Typography>
                        {day.activities.map((activity, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              mb: 1,
                              p: 1,
                              borderRadius: 1,
                              backgroundColor: 'grey.50',
                              '&:hover': {
                                backgroundColor: 'grey.100'
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                lineHeight: 1.4,
                                maxWidth: '100%'
                              }}
                            >
                              • {translateActivityText(activity)}
                            </Typography>
                            {onAskQuestion && (
                              <IconButton
                                size="small"
                                onClick={() => handleAskQuestionAboutActivity(day, activity, index)}
                                sx={{
                                  ml: 1,
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.light'
                                  }
                                }}
                                title={getText('askQuestion') || 'Ask Question'}
                              >
                                <QuestionAnswerIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        ))}

                        {day.materials && day.materials.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                              {getText('materialsNeeded')}:
                            </Typography>
                            {day.materials.map((material, index) => (
                              <Chip
                                key={index}
                                label={material}
                                size="small"
                                sx={{ mr: 1, mb: 1 }}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </CardContent>
          </Card>
        ))}

        {/* Stats */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center">
            <HeartIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="body2">
              {getText('stickersEarned')}: {Math.round(progress.stickers)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <TrophyIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="body2">
              {progress.completedActivities.length}/{totalDays} {getText('daysCompleted')}
            </Typography>
          </Box>
        </Box>

        {/* Debug Info - Remove after testing */}
        <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {getText('debug')}: showCompletionCelebration = {showCompletionCelebration.toString()}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          {getText('close')}
        </Button>
        {progress.day <= totalDays && (
          <Button
            onClick={handleCompleteDay}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {isLoading ? getText('saving') : getText('markAsDone')}
          </Button>
        )}
      </DialogActions>

      {/* Simple Confetti Animation */}
      {showConfetti && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: 'success.main',
              animation: 'bounce 0.6s ease-in-out',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0)',
                },
                '40%': {
                  transform: 'translateY(-30px)',
                },
                '60%': {
                  transform: 'translateY(-15px)',
                },
              },
            }}
          >
            🎉
          </Typography>
        </Box>
      )}



      {/* Debug Info - Keep for troubleshooting */}
      <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {getText('debug')}: showCompletionCelebration = {showCompletionCelebration.toString()}, {getText('progress')}: {progress.day}/{totalDays}
        </Typography>
      </Box>

    </Dialog>
  );
};



export default FlexibleRoadmap;
